import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, Modal, useTheme } from '@mui/material';
import { getSecurityAPI } from 'apis/policy/policyAPI';
import Tiptap from 'components/Tiptap/Tiptap';
import { useTranslation } from 'react-i18next';

interface SecurityAgreementModalProps {
  isAgreed: boolean;
  handleAgree: (securityPolicyId: number) => void;
}
interface SecurityData {
  securityPolicyId: number;
  security: string;
}
const SecurityAgreementModal = ({ isAgreed, handleAgree }: SecurityAgreementModalProps) => {
  const [securityData, setSecurityData] = useState<SecurityData | null>(null); // 보안서약 API내용
  const [error, setError] = useState<string | null>(null); // 에러메세지
  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t } = useTranslation(); // useTranslation 훅 사용
  // 다크 라이트 모드
  const theme = useTheme();

  const fetchData = async () => {
    try {
      const response = await getSecurityAPI(); // 보안서약 API호출
      setSecurityData(response.data); // 보안서약 상태 변경
    } catch (error) {
      setError('보안서약 약관을 불러오는데 실패하였습니다.');
    }
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [language]);

  if (error) {
    return <Typography color="error">{error}</Typography>; // Display error message if any error occurs
  }

  return (
    <Box padding={2} display="flex" flexDirection="column" gap={1}>
      <Box borderRadius={1} bgcolor={theme.palette.contentBlock.background} padding={2} display="flex" flexDirection="column" gap={2}>
        <Typography id="security-agreement-modal-title" variant="h5" component="h5" fontWeight="bold">
          {t('visit_request.security_modal.security_agree')}
        </Typography>
        {/* 데이터가 존재하지 않을 때 로딩 메시지 또는 스피너 표시 */}
        <Box>
          {!securityData || !securityData.security ? (
            <Typography> {t('visit_request.security_modal.loading')}</Typography>
          ) : (
            <Box boxShadow={1} borderRadius={1} bgcolor={theme.palette.tiptap.readonly}>
              <Tiptap content={securityData.security} editable={false} maxHeight="calc(100vh - 446px)" />
            </Box>
          )}
        </Box>
        {/* 동의 여부 Box */}
        <Box>
          <Typography id="security-agreement-modal-description" sx={{ mt: 2 }} color="primary">
            {t('visit_request.security_modal.security_content_1')}
          </Typography>
          <Typography id="security-agreement-modal-description" sx={{ mt: 1 }} color="primary">
            {t('visit_request.security_modal.security_content_2')}
          </Typography>
          <Box mt={3} display="flex" justifyContent="space-between">
            {securityData && securityData.security && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleAgree(securityData.securityPolicyId)}
                sx={{ width: '100px', borderRadius: '4px' }}
              >
                {t('visit_request.security_modal.agree')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SecurityAgreementModal;
